import React, { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

function KPI({ label, value, tone = "neutral", testId, money = false }) {
    const tones = {
        neutral: "bg-white border-gray-200 text-gray-900",
        open: "bg-green-50 border-green-200 text-green-900",
        awaiting: "bg-blue-50 border-blue-200 text-blue-900",
        near: "bg-amber-50 border-amber-200 text-amber-900",
        past: "bg-red-50 border-red-200 text-red-900",
    };
    return (
        <div
            className={`border rounded-sm p-4 ${tones[tone]}`}
            data-testid={testId}
        >
            <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">
                {label}
            </div>
            <div className="mt-1 text-2xl font-bold odr-mono">
                {money
                    ? `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : Number(value || 0).toLocaleString()}
            </div>
        </div>
    );
}

export default function Dashboard() {
    const [kpis, setKpis] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/dashboard/kpis")
            .then((r) => setKpis(r.data))
            .catch((e) => setError(e.message));
    }, []);

    return (
        <div data-testid="dashboard-page">
            <PageHeader
                title="Operational Dashboard"
                subtitle="Live operational workload — Pro-Pac Inc."
            />

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <KPI testId="kpi-open" label="Open Orders" value={kpis?.open_orders} tone="open" />
                <KPI
                    testId="kpi-awaiting"
                    label="Awaiting Shipment"
                    value={kpis?.awaiting_shipment}
                    tone="awaiting"
                />
                <KPI testId="kpi-late" label="Late Orders" value={kpis?.late_orders} tone="past" />
                <KPI testId="kpi-due-today" label="Due Today" value={kpis?.due_today} tone="near" />
                <KPI testId="kpi-due-week" label="Due This Week" value={kpis?.due_this_week} tone="near" />
                <KPI testId="kpi-overrun" label="Orders Overrun" value={kpis?.overrun} />
                <KPI testId="kpi-underrun" label="Orders Underrun" value={kpis?.underrun} />
                <KPI testId="kpi-late-produced" label="Produced Late" value={kpis?.produced_late} tone="past" />
            </div>

            <h3 className="mt-8 mb-3 text-xs uppercase tracking-wider font-semibold text-gray-600">
                Order Values
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                <KPI
                    testId="kpi-open-value"
                    label="Open Order Value"
                    value={kpis?.open_order_value}
                    money
                />
                <KPI
                    testId="kpi-in-house-value"
                    label="In-House Value"
                    value={kpis?.in_house_value}
                    money
                />
                <KPI
                    testId="kpi-farm-out-value"
                    label="Farm-Out Value"
                    value={kpis?.farm_out_value}
                    money
                />
            </div>

            <div className="mt-8 border border-gray-200 bg-white rounded-sm p-4">
                <div className="text-xs uppercase tracking-wider font-semibold text-gray-600">
                    Live Snapshot
                </div>
                <p className="text-sm text-gray-700 mt-1">
                    KPIs reflect the current state of all orders and production records.
                    {kpis?.as_of && (
                        <span className="text-gray-500">
                            {" "}
                            · Refreshed {new Date(kpis.as_of).toLocaleString()}
                        </span>
                    )}
                </p>
            </div>
        </div>
    );
}
